import {
	createSlice,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import { questionsReceived, selectAllQuestions } from "./questionsSlice";
import { pagesReceived, selectAllPages } from "./pagesSlice";
import { lookupsReceived, selectAllLookups } from "./lookupsSlice";
import { treesReceived, selectAllTrees } from "./treesSlice";
import { sectionsReceived, selectAllSections } from "./sectionsSlice";
import { answersReceived } from "./answersSlice";
import { rowsReceived } from "./rowsSlice";
import {
	formulasReceived,
	selectAllFormulas,
	formulasEdited,
} from "./formulasSlice";
import { dependencyReceived, selectAllDependencies } from "./dependenciesSlice";
import { newId } from "../utils/common";
import {
	getFormById,
	getFormDefinitionById,
	postFormDefinition,
	postResponseForm,
	postCPEForm as postCpeApi,
	getCPEFormById,
	getRequestFormById,
	postRequestForm as postRequestFormApi,
	saveByPayroll,
	getCMEFormById,
	saveByPayrollCpe,
} from "api";
import {
	formTeamEdited,
	selectFormTeam,
} from "../features/cme/formTeam/formTeamSlice";
import {
	cpeFormReceived,
	selectCpeFormData,
} from "../features/cme/cpeForm/cpeFormSlice";
import {
	requestLinesReceived,
	selectAllRequestLines,
} from "../features/cme/requestLine/requestLineSlice";
import {
	leaveLinesReceived,
	selectAllLeaveLines,
} from "../features/cme/leaveLine/leaveLineSlice";
import {
	attachmentReceived,
	selectAllAttachment as selectAllCmeAttachments,
} from "../features/cme/attachment/attachmentsSlice";
import {
	requestFormReceived,
	selectRequestFormData,
} from "../features/cme/requestForm/requestFormSlice";
import {
	fetchPermission,
	selectIsSysAdmin,
	selectIsCmeCoordinator,
	selectPersonId,
	selectUser,
} from "./securitySlice";
import {
	selectActiveWorkflowQuery,
	selectActiveWorkflow,
	workflowsReceived,
} from "./workflowsSlice";
import {
	childrenRequestLinesReceived,
	parentFormReceived,
	selectParentFormTypeId,
} from "../features/cme/parentForm/parentFormSlice";
import {
	balanceReceived,
	openCommitmentReceived,
} from "../features/cme/balance/balanceSlice";
import {
	postWorkflowHistoryThunk,
	workflowHistoryReceived,
} from "./workflowHistorySlice";
import { personEdited } from "../features/cme/person/personSlice";
import { CME_APP_ID } from "../features/cme/constants";
import {
	selectAdminActiveCpeData,
	selectAdminPerson,
} from "../features/cme/admin/adminSlice";
import { keyMembersReceived } from "../features/capex/keyMembers/keyMembersSlice";
import { selectAllInvalidQuestionsByCurrentPage, selectFormToPost } from ".";

const defaultOptions = { options: { isSilentSave: true } };

const initialState = {
	data: {
		id: newId(),
		personId: null, //for CME
		status: "Draft",
		ownerId: null,
		formId: null,
		appHierarchyId: null,
		organisationHierarchyId: null,
		createOn: null,
		createBy: null,
		modifyOn: null,
		modifyBy: null,
		owner: null,
		modifyByUser: null,
		createByUser: null,
	},

	//persisted state regarding form stage/status
	state: {
		isNew: true,
		isTouched: false, //any change sets this to true
		isLocked: false,
		initialisedRows: [], //to remember rows that have had their initial rows added
	},

	//transient state regarding current session
	session: {
		isSilentSave: false,
		currentPage: undefined,
		status: "idle",
		validationMessage: "",
	},

	//meta data that built this type of form
	definition: {
		name: "",
	},

	//app-level determination
	appType: "qna",
	appContext: null,

	error: null,
};

export const getTitleByFormTypeId = (formTypeId, parentFormTypeId = null) => {
	if (parentFormTypeId) {
		return "Claim on " + getFormName(parentFormTypeId);
	} else if (formTypeId === 2) {
		//no parentform but claim is always course and conf claim
		//return blank title, which forces user to edit it
		return "";
	} else return getFormName(formTypeId);
};

export const getFormName = (formTypeId) => {
	switch (formTypeId) {
		case 1:
			return "Course and Conference Request";
		case 2:
			return "CME Claim";
		case 3:
			return "Other CME Claim";
		case 4:
			return "IT Equipment Request";
		case 5:
			return "SMO Leave Request";
		case 8:
			return "WRE Claim";
		case 9:
			return "CPD Course Claim";
		case 10:
			return "CPD Other Claim";
		default:
			return "";
	}
};

export const validate = createAsyncThunk(
	"form/validate",
	async (_, { getState, dispatch }) => {
		const state = getState();
		const invalidQuestions = selectAllInvalidQuestionsByCurrentPage(state);
		const questions = invalidQuestions.map((q) => q?.title);
		const joinedText = questions.join(", ");
		const message =
			"Please correct the input errors on the following questions: " +
			joinedText;
		dispatch(
			sessionEdited({
				status: "validation error",
				validationMessage: message,
			})
		);
	}
);

//#region Standard QNA forms get/post
export const TESTONLY_postFormDefinition = createAsyncThunk(
	"form/TESTONLY_postFormDefinition",
	async (_, { getState }) => {
		try {
			const state = getState();
			const definition = selectFormDefinition(state);
			const lookups = selectAllLookups(state);
			const pages = selectAllPages(state);
			const sections = selectAllSections(state);
			const dependencies = selectAllDependencies(state);
			const questions = selectAllQuestions(state);
			const formulas = selectAllFormulas(state);
			const trees = selectAllTrees(state);

			await postFormDefinition({
				id: "E3508ECC-7901-4552-B7DF-9734CB97403F",
				appHierarchyId: definition.appHierarchyId,
				organisationHierarchyId:
					definition.organisationHierarchyId /* todo: compute org hierarchy by a default setting on form type. for e.g. we could have it dependent on a pick-list inside the form, or dependent on the current user's home/chosen hierarchy*/,
				originId: "E3508ECC-7901-4552-B7DF-9734CB97403F",
				version: 0,
				name: "Online CAPEX",
				ownerId: "DC207B33-8849-4152-86C3-C37FA1CAC4C8",
				data: {
					lookups,
					pages,
					questions,
					sections,
					dependencies,
					formulas,
					trees,
				},
				isLive: true,
				createBy: "CDE836A4-6155-4E4C-A4E0-120E939CAC82",
				modifyBy: "CDE836A4-6155-4E4C-A4E0-120E939CAC82",
			});
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const fetchFormById = createAsyncThunk(
	"form/fetchFormById",
	//need the thunkApi's getState to retrieve auth token
	async (formId, { rejectWithValue, dispatch }) => {
		try {
			const data = await getFormById({ formId });
			const { status, content, state } = data;
			//status of isLocked is recalculated on opening a form each time
			const isLocked = mapStatusToLock(status);
			dispatch(answersReceived(content.answers));
			dispatch(rowsReceived(content.rows));
			dispatch(formulasEdited(content.formulas));
			if (content?.keyMembers)
				dispatch(keyMembersReceived(content.keyMembers));
			dispatch(formStateEdited({ ...state, isLocked, isNew: false }));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchFormDefinitionById = createAsyncThunk(
	"form/fetchFormDefinitionById",
	//need the thunkApi's getState to retrieve auth token
	async (formDefinitionId, { rejectWithValue, dispatch }) => {
		try {
			const responseData = await getFormDefinitionById({
				formDefinitionId,
			});
			const { data } = responseData;
			// form definition data is split across other slices
			if (data.questions && data.questions.length > 0)
				dispatch(questionsReceived(data.questions));
			if (data.pages && data.pages.length > 0)
				dispatch(pagesReceived(data.pages));
			if (data.lookups && data.lookups.length > 0)
				dispatch(lookupsReceived(data.lookups));
			if (data.sections && data.sections.length > 0)
				dispatch(sectionsReceived(data.sections));
			if (data.trees && data.trees.length > 0)
				dispatch(treesReceived(data.trees));
			if (data.dependencies && data.dependencies.length > 0)
				dispatch(dependencyReceived(data.dependencies));
			if (data.formulas && data.formulas.length > 0)
				dispatch(formulasReceived(data.formulas));

			return responseData;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postForm = createAsyncThunk(
	"form/postForm",
	async (
		options = { isSilentSave: true },
		{ dispatch, getState, rejectWithValue }
	) => {
		const state = getState();
		const formToPost = selectFormToPost(state);
		try {
			dispatch(sessionEdited({ isSilentSave: options.isSilentSave }));
			await postResponseForm(formToPost);
			return new Promise((resolve, reject) => {
				resolve();
			});
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);
//#endregion Standard QNA forms get/post

//#region CME forms get/post

function mapStatusToLock(status) {
	//return a new isLock value based on status
	return status === "Pending" ||
		status === "Pending Approval" ||
		status === "Approved" ||
		status === "Closed"
		? true
		: false;
}

function mapCmeStatusToQnaStatus(statusId) {
	return statusId === 1
		? "Draft"
		: statusId === 2
		? "Pending"
		: statusId === 3
		? "Approved"
		: statusId === 4
		? "Closed"
		: statusId === 5
		? "Declined"
		: statusId === 6
		? "Cancelled"
		: `Unknown Status Id of ${statusId}`;
}

export const postCpeForm = createAsyncThunk(
	"form/postCpeForm",
	async ({ data, options }, { dispatch, getState, rejectWithValue }) => {
		try {
			dispatch(
				sessionEdited({
					isSilentSave: options.isSilentSave,
				})
			);
			const state = getState();
			const formTeam = selectFormTeam(state);
			const response = await postCpeApi({
				...data,
				formTeam,
			});

			dispatch(cpeFormReceived(response));

			//the owner of the form is put into person
			dispatch(personEdited(response.owner));

			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const { statusId, id, formId, owner } = response;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id,
				formId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postCpeFormComplete = createAsyncThunk(
	"form/postCpeFormComplete",
	async (
		{ options } = defaultOptions,
		{ dispatch, getState, rejectWithValue }
	) => {
		try {
			dispatch(sessionEdited({ isSilentSave: options.isSilentSave }));
			const state = getState();
			const cpeForm = selectCpeFormData(state);
			const requestLines = selectAllRequestLines(state);
			const leaveLines = selectAllLeaveLines(state);
			const attachments = selectAllCmeAttachments(state);
			const formTeam = selectFormTeam(state);
			const data = {
				...cpeForm,
				requestLines,
				leaveLines,
				attachments,
				formTeam,
			};
			const response = await postCpeApi(data);
			dispatch(cpeFormReceived(response));
			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const { statusId, id, formId, owner } = response;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id,
				formId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const isCpeFormTable = (formTypeId) => {
	return [1, 9].includes(formTypeId);
};

export const fetchCmeFormById = createAsyncThunk(
	"form/fetchCmeFormById",
	//need the thunkApi's getState to retrieve auth token
	async (id, { getState, rejectWithValue, dispatch }) => {
		try {
			const {
				requestLines,
				attachments,
				formTeam,
				leaveLines,
				balances,
				commitments,
				workflows,
				workflowHistories,
				parentForm,
				childrenRequestLines,
				prevLines,
				...data
			} = await getCMEFormById({ id });

			//since this api returns both cpe/request form, we need to check which we just received
			const { formTypeId } = data;
			if (isCpeFormTable(formTypeId)) dispatch(cpeFormReceived(data));
			else dispatch(requestFormReceived(data));

			//attach prevLines to request lines
			const requestLinesWithPrevLines = requestLines.map((r) => {
				const prevLine =
					prevLines && prevLines.length > 0
						? prevLines.find((pl) => pl.requestId === r.requestId)
						: null;
				return { ...r, prevLine };
			});

			//standard/shared data entities relatead to form
			dispatch(requestLinesReceived(requestLinesWithPrevLines));
			dispatch(formTeamEdited(formTeam));
			dispatch(attachmentReceived(attachments));
			dispatch(leaveLinesReceived(leaveLines));

			//balances are received here, because sometimes form data is requested by users who don't have direct access to this form
			//the access is granted via a workflow delegate, for example
			dispatch(balanceReceived(balances));
			dispatch(openCommitmentReceived(commitments));

			//same reason as above, the workflows are retrieved here
			dispatch(workflowsReceived(workflows));
			dispatch(workflowHistoryReceived(workflowHistories));

			//retrieve parent form and sibling form data
			dispatch(parentFormReceived(parentForm));
			dispatch(childrenRequestLinesReceived(childrenRequestLines));

			//the owner of the form is put into person
			dispatch(personEdited(data.owner));

			//need to check if current user is same as form user
			const state = getState();
			const myPersonId = selectPersonId(state);
			if (myPersonId !== data.owner.personId) {
				dispatch(
					fetchPermission({
						appId: CME_APP_ID,
						userId: data.owner.id,
					})
				);
			}

			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const {
				statusId,
				id: returnedId,
				formId: returnedFormId,
				owner,
			} = data;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id: returnedId,
				formId: returnedFormId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchCpeFormById = createAsyncThunk(
	"form/fetchCpeFormById",
	//need the thunkApi's getState to retrieve auth token
	async (id, { rejectWithValue, dispatch }) => {
		try {
			const {
				requestLines,
				attachments,
				formTeam,
				leaveLines,
				// formLink,
				...data
			} = await getCPEFormById({ id });
			dispatch(cpeFormReceived(data));
			dispatch(requestLinesReceived(requestLines));
			dispatch(formTeamEdited(formTeam));
			dispatch(attachmentReceived(attachments));
			dispatch(leaveLinesReceived(leaveLines));
			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const {
				statusId,
				id: returnedId,
				formId: returnedFormId,
				owner,
			} = data;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id: returnedId,
				formId: returnedFormId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchRequestFormById = createAsyncThunk(
	"form/fetchRequestFormById",
	//need the thunkApi's getState to retrieve auth token
	async ({ id, formId }, { rejectWithValue, dispatch }) => {
		try {
			const {
				requestLines,
				attachments,
				formTeam,
				leaveLines,
				// formLink,
				...data
			} = await getRequestFormById({ id, formId });
			dispatch(requestFormReceived(data));
			dispatch(leaveLinesReceived(leaveLines));
			dispatch(requestLinesReceived(requestLines));
			dispatch(formTeamEdited(formTeam));
			dispatch(attachmentReceived(attachments));

			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const {
				statusId,
				id: returnedId,
				formId: returnedFormId,
				owner,
			} = data;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id: returnedId,
				formId: returnedFormId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postRequestForm = createAsyncThunk(
	"form/postRequestForm",
	async ({ data, options }, { dispatch, getState, rejectWithValue }) => {
		try {
			dispatch(sessionEdited({ isSilentSave: options.isSilentSave }));
			const state = getState();
			const formTeam = selectFormTeam(state);
			const activeCpe = selectAdminActiveCpeData(state);
			const supplementaryData = JSON.stringify({ activeCpe });
			const parentFormTypeId = selectParentFormTypeId(state);
			const defaultTitle = getTitleByFormTypeId(
				data.formTypeId,
				parentFormTypeId
			);
			const response = await postRequestFormApi({
				...data,
				title: defaultTitle,
				formTeam,
				supplementaryData,
			});

			dispatch(requestFormReceived(response));
			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const { statusId, id, formId, owner } = response;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id,
				formId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			//the owner of the form is put into person
			dispatch(personEdited(owner));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postRequestFormComplete = createAsyncThunk(
	"form/postRequestFormComplete",
	async (
		{ options } = defaultOptions,
		{ dispatch, getState, rejectWithValue }
	) => {
		try {
			dispatch(
				sessionEdited({
					isSilentSave: options.isSilentSave,
				})
			);
			const state = getState();
			const requestForm = selectRequestFormData(state);
			const requestLines = selectAllRequestLines(state);
			const leaveLines = selectAllLeaveLines(state);
			const attachments = selectAllCmeAttachments(state);
			const formTeam = selectFormTeam(state);
			const data = {
				...requestForm,
				requestLines,
				leaveLines,
				attachments,
				formTeam,
			};
			const response = await postRequestFormApi(data);
			dispatch(requestFormReceived(response));
			dispatch(formStateEdited({ isNew: false }));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postByPayroll = createAsyncThunk(
	"form/postByPayroll",
	async (
		{ comments, options, isCpeForm } = defaultOptions,
		{ dispatch, getState, rejectWithValue }
	) => {
		try {
			dispatch(
				sessionEdited({
					isSilentSave: options.isSilentSave,
				})
			);
			const state = getState();
			const requestForm = selectRequestFormData(state);
			const cpeForm = selectCpeFormData(state);
			const requestLines = selectAllRequestLines(state);
			const leaveLines = selectAllLeaveLines(state);
			const attachments = selectAllCmeAttachments(state);
			const formTeam = selectFormTeam(state);
			const userId = selectUser(state)?.id;
			const mappedForm = isCpeForm ? cpeForm : requestForm;
			const data = {
				...mappedForm,
				// statusId: 6,
				requestLines,
				leaveLines,
				attachments,
				formTeam,
			};
			const response = isCpeForm
				? await saveByPayrollCpe(data)
				: await saveByPayroll(data);
			if (comments) {
				dispatch(
					postWorkflowHistoryThunk({
						id: newId(),
						responseFormId: requestForm.id,
						action: "Admin Form Edit",
						description:
							"Form updated by an administrator after approval",
						category: "Admin",
						comment: comments,
						userId,
					})
				);
			}
			dispatch(requestFormReceived(response));
			//we map from cme data to qna form state
			//so that we can use common features like loading messages
			const { statusId, id, formId, owner } = response;
			const status = mapCmeStatusToQnaStatus(statusId);
			const isLocked = mapStatusToLock(status);
			const newData = {
				id,
				formId,
				owner,
				status,
			};
			dispatch(formDataEdited(newData));
			dispatch(formStateEdited({ isLocked, isNew: false }));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

//#endregion CME forms

const formSlice = createSlice({
	name: "form",
	initialState,
	reducers: {
		rowInitialised(state, action) {
			const initialisedRows = state.state.initialisedRows;
			state.state.initialisedRows = [...initialisedRows, action.payload];
		},
		formDataEdited(state, action) {
			Object.entries(action.payload).forEach(([key, value]) => {
				state.data[key] = value;
			});
		},
		formStateEdited(state, action) {
			Object.entries(action.payload).forEach(([key, value]) => {
				state.state[key] = value;
			});
		},
		formDefinitionEdited(state, action) {
			state.definition = { ...action.payload };
		},
		sessionEdited(state, action) {
			state.session = { ...state.session, ...action.payload };
		},
		errorEdited(state, action) {
			state.error = action.payload;
		},
		lockToggled(state, action) {
			state.state.isLocked = !state.state.isLocked;
		},
		statusEdited(state, action) {
			state.data.status = action.payload;
			if (state.appType === "cme") {
				state.state.isLocked = mapStatusToLock(action.payload);
			}
		},
		appTypeEdited(state, action) {
			state.appType = action.payload;
		},
		appContextEdited(state, action) {
			state.appContext = action.payload;
		},
		idGenerated(state, action) {
			state.data.id = newId();
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			//restore back to initial state, but keep the original app type & context
			return {
				...initialState,
				appContext: state.appContext,
				appType: state.appType,
			};
		},
		[fetchFormDefinitionById.pending]: (state, action) => {
			// state.session.status = "loading";
			state.error = null;
		},
		[fetchFormDefinitionById.fulfilled]: (state, action) => {
			// state.session.status = "succeeded";
			Object.entries(action.payload).forEach(([key, value]) => {
				/* skip the data property, which is already dispatched into their
					own slices via the thunk */
				if (key !== "data") state.definition[key] = value;
			});
			state.error = null;
		},
		[fetchFormDefinitionById.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[postForm.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[postForm.fulfilled]: (state, action) => {
			state.session.status = state.session.isSilentSave
				? "succeeded"
				: "saved";
			state.error = null;
		},
		[postForm.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[postCpeForm.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[postCpeForm.fulfilled]: (state, action) => {
			state.session.status = state.session.isSilentSave
				? "succeeded"
				: "saved";
			state.error = null;
		},
		[postCpeForm.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[postCpeFormComplete.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[postCpeFormComplete.fulfilled]: (state, action) => {
			state.session.status = state.session.isSilentSave
				? "succeeded"
				: "saved";
			state.error = null;
		},
		[postCpeFormComplete.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[fetchFormById.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[fetchFormById.fulfilled]: (state, action) => {
			state.session.status = "succeeded";
			Object.entries(action.payload).forEach(([key, value]) => {
				/* skip the data property, which is already dispatched into their
					own slices via the thunk */
				if (key !== "content" && key !== "authoriseData")
					state.data[key] = value;
			});
			state.error = null;
		},
		[fetchFormById.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[fetchCmeFormById.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[fetchCmeFormById.fulfilled]: (state, action) => {
			state.session.status = "idle";
			state.error = null;
		},
		[fetchCmeFormById.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[fetchCpeFormById.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[fetchCpeFormById.fulfilled]: (state, action) => {
			state.session.status = "idle";
			state.error = null;
		},
		[fetchCpeFormById.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[postRequestForm.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[postRequestForm.fulfilled]: (state, action) => {
			state.session.status = state.session.isSilentSave
				? "succeeded"
				: "saved";
			state.error = null;
		},
		[postRequestForm.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[fetchRequestFormById.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[fetchRequestFormById.fulfilled]: (state, action) => {
			state.session.status = "idle";
			state.error = null;
		},
		[fetchRequestFormById.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[postRequestFormComplete.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[postRequestFormComplete.fulfilled]: (state, action) => {
			state.session.status = state.session.isSilentSave
				? "succeeded"
				: "saved";
			state.error = null;
		},
		[postByPayroll.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
		[postByPayroll.pending]: (state, action) => {
			state.session.status = "loading";
			state.error = null;
		},
		[postByPayroll.fulfilled]: (state, action) => {
			state.session.status = state.session.isSilentSave
				? "succeeded"
				: "saved";
			state.error = null;
		},
		[postRequestFormComplete.rejected]: (state, action) => {
			state.session.status = "failed";
			state.error = action.payload;
		},
	},
});

export const {
	formDataEdited,
	formStateEdited,
	sessionEdited,
	errorEdited,
	lockToggled,
	statusEdited,
	formDefinitionEdited,
	appContextEdited,
	rowInitialised,
	idGenerated,
} = formSlice.actions;

export const selectForm = (state) => state.form.data;
export const selectFormId = (state) => state.form.data.id;
export const selectCmeFormId = (state) => state.form.data.formId;
export const selectFormState = (state) => state.form.state;
export const selectFormStateInitialisedRows = (state) =>
	state.form.state.initialisedRows;
export const selectFormDefinition = (state) => state.form.definition;
export const selectFormError = (state) => state.form.error;
export const selectSession = (state) => state.form.session;
export const selectFormStatus = (state) => state.form.session.status;
export const selectAppHierarchyId = (state) =>
	state.form.data.appHierarchyId
		? state.form.data.appHierarchyId.toUpperCase()
		: null;
export const selectAppContext = (state) => state.form.appContext;
export const selectFormPersonId = createSelector(
	[selectCpeFormData, selectRequestFormData],
	(c, r) => {
		return c.personId ? c.personId : r.personId;
	}
);
export const selectPersonIdForNewForm = createSelector(
	[selectAdminPerson, selectPersonId],
	(adminPerson, myPersonId) => {
		return adminPerson?.personId || myPersonId;
	}
);

export const selectFormOwner = (state) => state.form.data.owner;

export const selectNotMyApproval = createSelector(
	[selectActiveWorkflow, selectUser, selectFormOwner],
	(w, u, o) => {
		//active workflow user is not me
		//AND i am not the form owner
		//AND check user and form objects exist
		if (w?.user?.id !== u?.id && u?.id !== o?.id && u && o) {
			return true;
		} else return false;
	}
);

export const selectQnaIsOverrideUnlocked = (state) =>
	state.form.state.isOverrideUnlocked;

export const selectQnaIsLocked = (state) => state.form.state.isLocked;

export const selectIsLocked = createSelector(
	[
		(state) => state.form.state.isLocked,
		selectFormOwner,
		selectUser,
		selectActiveWorkflowQuery,
		selectIsSysAdmin,
	],
	(isLocked, owner, user, queryWorkflow, isSysAdmin) => {
		if (user?.id === queryWorkflow?.user?.id && owner?.id === user?.id) {
			//queried user is this current user
			//current user is the form owner
			//they can edit
			return false;
		} else if (!isLocked && isSysAdmin) {
			//form is naturally unlocked and user is system admin
			//allow them to edit
			return false;
		} else {
			//default check
			return isLocked || user?.id !== owner?.id;
		}
	}
);

export const selectIsCmeLocked = createSelector(
	[
		selectIsLocked,
		selectCpeFormData,
		selectRequestFormData,
		selectIsCmeCoordinator,
	],
	(isLocked, cpeForm, requestForm, amCmeCoordinator) => {
		//firstly, if it's already unlocked, just return it
		if (!isLocked) {
			return isLocked;
		}

		const form = cpeForm.personId ? cpeForm : requestForm;
		if (
			[1, 5, 7].includes(form.formTypeId) && //if type of form is course request, smo leave request, or leave claim (actual)
			amCmeCoordinator && //and current user is a cme coordinator on an existing workflow
			![3, 4].includes(form.statusId) //NOT in approved or finalised status
		) {
			return false; //allow the cme coordinator to edit the leave form
		} else {
			return isLocked; //in all other cases, return the default isLocked value
		}
	}
);

export default formSlice.reducer;
